import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TenantLogo from '../components/TenantLogo';
import BrandedWordmark from '../components/BrandedWordmark';
import {
  ensureTenantBrandingDefaults,
  resolveBrandingFromParsed,
  type ResolvedBranding
} from '../utils/brandingStorage';
import {
  configToBrandingProfile,
  fetchAndCacheTenantConfig,
  getCachedTenantConfig,
  SSO_LOGIN_BUTTON_LABEL,
  type PublicTenantConfigDTO
} from '../utils/tenantConfig';

const Login: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [signInStep, setSignInStep] = useState<'email' | 'password' | 'sso'>('email');
  const [hintReason, setHintReason] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [branding, setBranding] = useState<ResolvedBranding>(() => ensureTenantBrandingDefaults());
  const [loginLogoFailed, setLoginLogoFailed] = useState(false);
  const [tenantConfig, setTenantConfig] = useState<PublicTenantConfigDTO | null>(() =>
    getCachedTenantConfig()
  );
  const [ssoLoginUrl, setSsoLoginUrl] = useState<string | null>(null);

  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const { colors, setCustomColors, setTheme, resolvedMode } = useTheme();
  const navigate = useNavigate();
  const isDark = resolvedMode === 'dark';
  const loginSurface = isDark ? '#12171f' : '#f8f9fc';
  const fieldSurface = isDark ? '#1b222c' : '#ffffff';
  const fieldBorder = isDark ? '#3b4654' : '#e5e7eb';
  const fieldHoverBorder = isDark ? '#657184' : '#d1d5db';
  const headingColor = isDark ? '#f3f4f6' : '#1a1a2e';
  const bodyColor = isDark ? '#c4c9d1' : '#6b7280';
  const labelColor = isDark ? '#d5d9df' : '#374151';
  const mutedColor = isDark ? '#8f99a8' : '#9ca3af';
  const themeActionsRef = React.useRef({ setCustomColors, setTheme });
  themeActionsRef.current = { setCustomColors, setTheme };
  const lastAppliedThemeRef = React.useRef<string>('');

  const applyTenantConfig = React.useCallback((cfg: PublicTenantConfigDTO) => {
    setTenantConfig(cfg);
    setLoginLogoFailed(false);
    const profile = configToBrandingProfile(cfg);
    setBranding(
      resolveBrandingFromParsed({
        companyName: profile.companyName,
        companyTagline: profile.companyTagline,
        logo: profile.logo,
        logoMark: profile.logoMark,
        logoOnDark: profile.logoOnDark
      })
    );
    const themeKey = `${profile.themeColors.primary}|${profile.themeColors.secondary}`;
    if (lastAppliedThemeRef.current !== themeKey) {
      lastAppliedThemeRef.current = themeKey;
      void themeActionsRef.current.setCustomColors(profile.themeColors);
      void themeActionsRef.current.setTheme('custom');
    }
    const ssoEnabled = cfg.loginMethods.includes('sso') && Boolean(cfg.ssoLoginUrl);
    setSsoLoginUrl(ssoEnabled ? cfg.ssoLoginUrl : null);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const cached = getCachedTenantConfig();
    if (cached) {
      applyTenantConfig(cached);
    }
    (async () => {
      try {
        const cfg = await fetchAndCacheTenantConfig();
        if (!cancelled) {
          applyTenantConfig(cfg);
        }
      } catch (e) {
        console.error('Error loading tenant config:', e);
        if (!cancelled) {
          setBranding(ensureTenantBrandingDefaults());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyTenantConfig]);

  React.useEffect(() => {
    document.title = branding.companyName;
  }, [branding.companyName]);

  React.useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  React.useEffect(() => {
    const samlErr = searchParams.get('saml_error');
    if (samlErr) {
      setSignInStep('sso');
      setError(`${samlErr} Sign-in did not finish. Stay on this page and try again, or use a break-glass password.`);
      const next = new URLSearchParams(searchParams);
      next.delete('saml_error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Enter your email or username.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/auth/login-hint?identifier=${encodeURIComponent(username.trim())}`);
      const body = await response.json();
      const method = body?.data?.method === 'sso' ? 'sso' : 'password';
      setHintReason(String(body?.data?.reason || ''));
      setSignInStep(method);
    } catch {
      setHintReason('This account uses a local password.');
      setSignInStep('password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const success = await login(username, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Invalid username or password. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const showPasswordLogin = !tenantConfig || tenantConfig.loginMethods.includes('password');
  const showSsoLogin = Boolean(ssoLoginUrl);
  const handleSsoLogin = () => {
    // Always use SP-initiated login on this tenant. Direct Okta/Entra app URLs are
    // IdP-initiated and post to whatever ACS is saved in the IdP (often a dead
    // azurewebsites host) — that is why Sign in with SSO was landing on
    // turboiam-provysn-dev-be.azurewebsites.net/.../acs.
    const spLogin = `${window.location.origin.replace(/\/$/, '')}/api/v1/auth/saml/login`;
    window.location.href = spLogin;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: loginSurface }}>

      {/* ── Left branding panel ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '52%',
          background: isDark
            ? `linear-gradient(135deg, #0d1117 0%, ${colors.dark}55 45%, #111d2a 100%)`
            : `linear-gradient(135deg, #f8fafc 0%, ${colors.light}88 45%, #e0f2fe 100%)`,
          px: 7,
          py: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid pattern overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(${isDark ? 'rgba(255,255,255,0.045)' : 'rgba(15,23,42,0.04)'} 1px, transparent 1px),
              linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.045)' : 'rgba(15,23,42,0.04)'} 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
            pointerEvents: 'none',
          }}
        />
        {/* Large decorative circle */}
        <Box
          sx={{
            position: 'absolute',
            width: 480,
            height: 480,
            borderRadius: '50%',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,23,42,0.08)'}`,
            top: '50%',
            right: -160,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,23,42,0.08)'}`,
            top: '50%',
            right: -60,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative' }}>
          <TenantLogo
            branding={branding}
            variant="login"
            failed={loginLogoFailed}
            onError={() => setLoginLogoFailed(true)}
            wordmarkTone={isDark ? 'onPrimary' : 'default'}
            wordmarkSx={{ color: isDark ? '#f3f4f6' : '#0f172a', fontWeight: 700, fontSize: '1.15rem', letterSpacing: 0.3 }}
          />
        </Box>

        {/* Central messaging */}
        <Box sx={{ position: 'relative' }}>
          <Typography
            sx={{
              color: isDark ? 'rgba(226,232,240,0.65)' : 'rgba(15,23,42,0.55)',
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            SSO · IGA · PAM
          </Typography>
          <Typography
            sx={{
              color: isDark ? '#f8fafc' : '#0f172a',
              fontSize: '2.4rem',
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 2.5,
              letterSpacing: -0.5,
            }}
          >
            Onboard apps fast.<br />Stay secure.<br />Stay in control.
          </Typography>
          <Typography
            component="p"
            sx={{
              color: isDark ? 'rgba(226,232,240,0.78)' : 'rgba(15,23,42,0.7)',
              fontSize: '0.95rem',
              lineHeight: 1.7,
              maxWidth: 360,
            }}
          >
            <Box component="span" sx={{ display: 'inline' }}>
              <BrandedWordmark
                name={branding.companyName}
                variant="body1"
                component="span"
                sx={{ display: 'inline-flex', verticalAlign: 'baseline' }}
                baseSx={{ fontSize: '0.95rem', color: isDark ? 'rgba(226,232,240,0.78)' : 'rgba(15,23,42,0.7)', fontWeight: 600 }}
              />
            </Box>
            {' '}accelerates onboarding to IAM platforms — with built-in risk assessment, compliance tracking, and centralized access governance across your entire application portfolio.
          </Typography>
        </Box>

      </Box>

      {/* ── Right form panel ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: loginSurface,
          px: { xs: 3, sm: 6, md: 8 },
          py: 6,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>

          {/* Mobile-only logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 4 }}>
            <TenantLogo
              branding={branding}
              variant="compact"
              failed={loginLogoFailed}
              onError={() => setLoginLogoFailed(true)}
              wordmarkSx={{ fontWeight: 700, fontSize: '1.05rem', color: headingColor }}
            />
          </Box>

          {/* Form heading */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: headingColor, mb: 0.5, letterSpacing: -0.3 }}>
              Welcome back
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: bodyColor }}>
              {tenantConfig?.welcomeText || 'Sign in to your account to continue'}
            </Typography>
          </Box>

          {/* Error alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2, fontSize: '0.82rem', py: 0.5 }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={signInStep === 'email' ? handleContinue : handleSubmit}>
            {signInStep === 'email' && (
              <>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: labelColor, mb: 0.75 }}>
                  Email or username
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="username"
                  name="username"
                  placeholder="Enter your work email"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  size="small"
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      bgcolor: fieldSurface,
                      fontSize: '0.9rem',
                      color: headingColor,
                      '& fieldset': { borderColor: fieldBorder },
                      '&:hover fieldset': { borderColor: fieldHoverBorder },
                      '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
                    },
                    '& .MuiInputBase-input': { py: 1.25 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ fontSize: 18, color: mutedColor }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{
                    py: 1.25,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    bgcolor: colors.primary,
                    color: 'white',
                    textTransform: 'none',
                  }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
                </Button>
                {showSsoLogin && (
                  <>
                    <Divider sx={{ my: 2.5, color: fieldBorder, '&::before, &::after': { borderColor: fieldBorder } }}>
                      <Typography sx={{ fontSize: '0.75rem', color: mutedColor, px: 1 }}>or</Typography>
                    </Divider>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      onClick={handleSsoLogin}
                      sx={{
                        py: 1.15,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        borderRadius: '8px',
                        textTransform: 'none',
                        borderColor: fieldHoverBorder,
                        color: headingColor,
                        bgcolor: fieldSurface,
                        '&:hover': {
                          borderColor: colors.primary,
                          bgcolor: `${colors.primary}08`,
                        },
                      }}
                    >
                      {SSO_LOGIN_BUTTON_LABEL}
                    </Button>
                  </>
                )}
              </>
            )}
            {signInStep === 'password' && showPasswordLogin && (
              <>
            <Typography sx={{ fontSize: '0.78rem', color: bodyColor, mb: 1.5 }}>{hintReason}</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: labelColor, mb: 0.75 }}>
              Username
            </Typography>
            <TextField
              required
              fullWidth
              id="username"
              name="username"
              placeholder="Enter your username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={e => setUsername(e.target.value)}
              size="small"
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: fieldSurface,
                  fontSize: '0.9rem',
                  color: headingColor,
                  '& fieldset': { borderColor: fieldBorder },
                  '&:hover fieldset': { borderColor: fieldHoverBorder },
                  '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
                },
                '& .MuiInputBase-input': { py: 1.25 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ fontSize: 18, color: mutedColor }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: labelColor }}>
                Password
              </Typography>
            </Box>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              size="small"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: fieldSurface,
                  fontSize: '0.9rem',
                  color: headingColor,
                  '& fieldset': { borderColor: fieldBorder },
                  '&:hover fieldset': { borderColor: fieldHoverBorder },
                  '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
                },
                '& .MuiInputBase-input': { py: 1.25 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ fontSize: 18, color: mutedColor }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(p => !p)}
                      edge="end"
                      size="small"
                      sx={{ color: mutedColor }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.25,
                fontSize: '0.9rem',
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: colors.primary,
                color: 'white',
                textTransform: 'none',
                boxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 4px 12px ${colors.primary}30`,
                '&:hover': {
                  bgcolor: colors.dark,
                  boxShadow: `0 4px 16px ${colors.primary}50`,
                },
                '&:disabled': {
                  bgcolor: isDark ? '#303844' : '#e5e7eb',
                  color: mutedColor,
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
            </Button>
            <Button type="button" fullWidth onClick={() => { setSignInStep('email'); setError(''); }} sx={{ mt: 1, textTransform: 'none', color: bodyColor }}>
              Use a different email
            </Button>
              </>
            )}

            {signInStep === 'sso' && (
              <>
                <Typography sx={{ fontSize: '0.82rem', color: bodyColor, mb: 2 }}>
                  {hintReason || 'This email continues at the company sign-in. If that times out, stay here and use a break-glass password.'}
                </Typography>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  onClick={handleSsoLogin}
                  sx={{ py: 1.15, fontSize: '0.9rem', fontWeight: 600, borderRadius: '8px', textTransform: 'none', bgcolor: colors.primary }}
                >
                  {SSO_LOGIN_BUTTON_LABEL}
                </Button>
                <Button type="button" fullWidth onClick={() => setSignInStep('password')} sx={{ mt: 1, textTransform: 'none', color: bodyColor }}>
                  Use a break-glass password
                </Button>
                <Button type="button" fullWidth onClick={() => { setSignInStep('email'); setError(''); }} sx={{ textTransform: 'none', color: mutedColor }}>
                  Return to email
                </Button>
              </>
            )}

            {signInStep === 'password' && showSsoLogin && (
              <>
                {showPasswordLogin && (
                  <Divider sx={{ my: 2.5, color: fieldBorder, '&::before, &::after': { borderColor: fieldBorder } }}>
                    <Typography sx={{ fontSize: '0.75rem', color: mutedColor, px: 1 }}>or</Typography>
                  </Divider>
                )}
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleSsoLogin}
                  sx={{
                    py: 1.15,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    textTransform: 'none',
                    borderColor: fieldHoverBorder,
                    color: headingColor,
                    bgcolor: fieldSurface,
                    '&:hover': {
                      borderColor: colors.primary,
                      bgcolor: `${colors.primary}08`,
                    },
                  }}
                >
                  {SSO_LOGIN_BUTTON_LABEL}
                </Button>
              </>
            )}
          </Box>

          {/* Footer */}
          <Divider sx={{ my: 3, borderColor: fieldBorder }} />
          <Box sx={{ textAlign: 'center' }}>
            {process.env.NODE_ENV === 'development' && (
              <Typography sx={{ fontSize: '0.75rem', color: mutedColor, mb: 1 }}>
                Dev: <strong style={{ color: bodyColor }}>superadmin</strong> / <strong style={{ color: bodyColor }}>CHANGE_ME_IN_PRODUCTION</strong>
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.72rem', color: mutedColor, display: 'flex', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap', gap: 0.25 }}>
              © {new Date().getFullYear()}{' '}
              <BrandedWordmark
                name={branding.companyName}
                variant="caption"
                sx={{ color: mutedColor }}
                baseSx={{ fontSize: '0.72rem', color: mutedColor }}
                suffixSx={{ fontSize: '0.72rem', color: mutedColor }}
              />
              . All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
